import { useState } from 'react';
import './index.css';
import { FiArrowRight, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';

const TeamBio = ({ members = [], header = "Meet the team", isEditing, onChange, uploadImage }) => {

    const safeMembers = Array.isArray(members) ? members : [];

    const [selectedMember, setSelectedMember] = useState(null);
    const [uploadingIndex, setUploadingIndex] = useState(null);

    const getMemberIndex = (member) =>
        safeMembers.findIndex(
            (m) => m.id === member.id || m.imageKey === member.imageKey || m.name === member.name
        );

    const updateMember = (index, field, value) => {
        const updated = [...safeMembers];
        updated[index] = { ...updated[index], [field]: value };

        if (selectedMember && index === getMemberIndex(selectedMember)) {
            setSelectedMember(updated[index]);
        }

        onChange({ header, members: updated });
    };

    const handleFileUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploadingIndex(index);

            const preview = URL.createObjectURL(file);

            const temp = [...safeMembers];
            temp[index] = { ...temp[index], photo: preview };

            onChange({ header, members: temp });

            const { key, url } = await uploadImage(file);

            const updated = [...temp];
            updated[index] = {
                ...updated[index],
                photo: url,
                imageKey: key
            };

            if (selectedMember && index === getMemberIndex(selectedMember)) {
                setSelectedMember(updated[index]);
            }

            onChange({ header, members: updated });

        } catch (err) {
            console.error("❌ Upload failed:", err);
        } finally {
            setUploadingIndex(null);
        }
    };

    const addMember = () => {
        onChange({
            header,
            members: [
                ...safeMembers,
                {
                    id: Date.now(),
                    name: '',
                    linkedin: '',
                    position: '',
                    location: '',
                    startDate: '',
                    photo: '',
                    imageKey: '',
                    bio: ''
                }
            ]
        });
    };

    const removeMember = (index, e) => {
        e.stopPropagation();
        if (window.confirm("Remove this member?")) {
            onChange({
                header,
                members: safeMembers.filter((_, i) => i !== index)
            });
        }
    };

    return (
        <div className='tb-section'>
            <div className="tb-header">
                {isEditing ? (
                    <input
                        className="demo-input small"
                        placeholder="Section Title (e.g., Meet the Team)"
                        value={header}
                        onChange={(e) =>
                            onChange({
                                header: e.target.value,
                                members: safeMembers
                            })
                        }
                    />
                ) : (
                    <h1 className="tb-main-title">{header}</h1>
                )}

                {isEditing && (
                    <button className="tb-add-btn" onClick={addMember}>
                        <FiPlus /> Add Member
                    </button>
                )}
            </div>

            <div className="tb-grid">
                {safeMembers.map((member, index) => (
                    <div
                        key={member.id || index}
                        className={`tb-card ${isEditing ? 'tb-edit-mode' : ''}`}
                        onClick={() => !isEditing && setSelectedMember(member)}
                    >
                        {isEditing && (
                            <button className="tb-trash-large" onClick={(e) => removeMember(index, e)}>
                                <FiTrash2 size={20} />
                            </button>
                        )}

                        <div className="tb-img-wrapper">
                            {uploadingIndex === index && (
                                <div className="tb-img-loader">Uploading...</div>
                            )}

                            <img
                                src={member.photo || "/default-user.png"}
                                alt={member.name}
                                className={`tb-img ${uploadingIndex === index ? "tb-blur" : ""}`}
                            />
                        </div>

                        {isEditing ? (
                            <div onClick={(e) => e.stopPropagation()}>
                                <input
                                    className="tb-edit-input tb-bold"
                                    placeholder="Full Name"
                                    value={member.name}
                                    onChange={(e) => updateMember(index, 'name', e.target.value)}
                                />

                                <input
                                    className="tb-edit-input"
                                    placeholder="LinkedIn Profile URL"
                                    value={member.linkedin || ""}
                                    onChange={(e) => updateMember(index, 'linkedin', e.target.value)}
                                />

                                <input
                                    className="tb-edit-input"
                                    placeholder="Job Title / Position"
                                    value={member.position}
                                    onChange={(e) => updateMember(index, 'position', e.target.value)}
                                />

                                <label className="tb-upload-button">
                                    <FiUpload /> Upload Photo
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => handleFileUpload(e, index)}
                                    />
                                </label>

                                <button
                                    className="tb-full-edit-btn"
                                    onClick={() => setSelectedMember(member)}
                                >
                                    Edit Details & Bio
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="tb-name">{member.name}</h3>

                                {member.linkedin && (
                                    <p className="tb-pos-row">
                                        <a
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tb-linkedin"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Linkedin Profile
                                        </a>
                                    </p>
                                )}

                                <p className="tb-pos">{member.position}</p>
                                <FiArrowRight className="tb-arrow" />
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* MODAL */}
            {selectedMember && (
                <div className="tb-modal-overlay" onClick={() => setSelectedMember(null)}>
                    <div className="tb-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="tb-close" onClick={() => setSelectedMember(null)}>×</button>

                        <div className="tb-modal-flex">
                            <div className="tb-modal-left">
                                <div className="tb-img-wrapper">
                                    <img
                                        src={selectedMember.photo || "/default-user.png"}
                                        className="tb-modal-img"
                                    />
                                </div>

                                {isEditing && (
                                    <label className="tb-upload-button">
                                        <FiUpload /> Upload Photo
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) =>
                                                handleFileUpload(e, getMemberIndex(selectedMember))
                                            }
                                        />
                                    </label>
                                )}
                            </div>

                            <div className="tb-modal-right">
                                <input
                                    className="tb-edit-input"
                                    placeholder="Full Name"
                                    value={selectedMember.name}
                                    onChange={(e) =>
                                        updateMember(getMemberIndex(selectedMember), 'name', e.target.value)
                                    }
                                />

                                <input
                                    className="tb-edit-input"
                                    placeholder="Job Title / Position"
                                    value={selectedMember.position}
                                    onChange={(e) =>
                                        updateMember(getMemberIndex(selectedMember), 'position', e.target.value)
                                    }
                                />

                                <input
                                    className="tb-edit-input"
                                    placeholder="LinkedIn Profile URL"
                                    value={selectedMember.linkedin || ""}
                                    onChange={(e) =>
                                        updateMember(getMemberIndex(selectedMember), 'linkedin', e.target.value)
                                    }
                                />

                                <textarea
                                    className="tb-edit-input tb-textarea"
                                    placeholder="Write a short bio about the team member..."
                                    value={selectedMember.bio}
                                    onChange={(e) =>
                                        updateMember(getMemberIndex(selectedMember), 'bio', e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamBio;